export const templates = {
  'scratch': `openapi: 3.0.0
info:
  title: My API
  version: 1.0.0
  description: API description
paths: {}`,

  'users-crud': `openapi: 3.0.0
info:
  title: User Management API
  version: 1.0.0
  description: |
    Complete CRUD API for user management with authentication and authorization.
    
    ## Rate Limiting
    1000 requests per hour per API key.
  contact:
    name: API Support
    email: support@example.com
  license:
    name: MIT
    url: https://opensource.org/licenses/MIT
servers:
  - url: https://api.example.com/v1
    description: Production server
tags:
  - name: users
    description: User management operations
  - name: auth
    description: Authentication operations
paths:
  /users:
    get:
      summary: List all users
      description: Returns a paginated list of users
      operationId: listUsers
      tags:
        - users
      parameters:
        - name: page
          in: query
          schema:
            type: integer
            default: 1
          description: Page number
        - name: limit
          in: query
          schema:
            type: integer
            default: 20
          description: Items per page
      responses:
        '200':
          description: Success
          content:
            application/json:
              schema:
                type: object
                properties:
                  data:
                    type: array
                    items:
                      $ref: '#/components/schemas/User'
                  page:
                    type: integer
                  total:
                    type: integer
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
    post:
      summary: Create a new user
      description: Creates a new user account
      operationId: createUser
      tags:
        - users
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/UserCreate'
      responses:
        '201':
          description: User created
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '400':
          description: Bad request
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /users/{id}:
    get:
      summary: Get user by ID
      description: Returns a single user
      operationId: getUserById
      tags:
        - users
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: User ID
      responses:
        '200':
          description: Success
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '404':
          description: User not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
    put:
      summary: Update user
      description: Updates an existing user
      operationId: updateUser
      tags:
        - users
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: User ID
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/UserUpdate'
      responses:
        '200':
          description: User updated
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '400':
          description: Bad request
        '404':
          description: User not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
    delete:
      summary: Delete user
      description: Deletes a user account
      operationId: deleteUser
      tags:
        - users
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: User ID
      responses:
        '204':
          description: User deleted
        '404':
          description: User not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /auth/login:
    post:
      summary: User login
      description: Authenticate user and return API key
      operationId: login
      tags:
        - auth
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              required:
                - email
                - password
              properties:
                email:
                  type: string
                  format: email
                password:
                  type: string
                  format: password
      responses:
        '200':
          description: Login successful
          content:
            application/json:
              schema:
                type: object
                properties:
                  apiKey:
                    type: string
                  user:
                    $ref: '#/components/schemas/User'
        '401':
          description: Invalid credentials
components:
  schemas:
    User:
      type: object
      required:
        - id
        - email
        - name
      properties:
        id:
          type: string
          description: User ID
        email:
          type: string
          format: email
          description: User email address
        name:
          type: string
          description: User full name
        role:
          type: string
          enum: [admin, user, guest]
          description: User role
        createdAt:
          type: string
          format: date-time
          description: Account creation timestamp
    UserCreate:
      type: object
      required:
        - email
        - name
        - password
      properties:
        email:
          type: string
          format: email
        name:
          type: string
        password:
          type: string
          format: password
        role:
          type: string
          enum: [admin, user, guest]
          default: user
    UserUpdate:
      type: object
      properties:
        email:
          type: string
          format: email
        name:
          type: string
        role:
          type: string
          enum: [admin, user, guest]
  securitySchemes:
    ApiKeyAuth:
      type: apiKey
      in: header
      name: X-API-Key`,

  'payments': `openapi: 3.0.0
info:
  title: Payment System API
  version: 1.0.0
  description: |
    Payment processing API with transactions, refunds, and webhooks.
    
    ## Rate Limiting
    500 requests per hour per API key.
  contact:
    name: Payment Support
    email: payments@example.com
servers:
  - url: https://api.payments.example.com/v1
    description: Production server
tags:
  - name: payments
    description: Payment operations
  - name: refunds
    description: Refund operations
  - name: webhooks
    description: Webhook management
paths:
  /payments:
    post:
      summary: Create payment
      description: Process a new payment
      operationId: createPayment
      tags:
        - payments
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/PaymentRequest'
      responses:
        '201':
          description: Payment created
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Payment'
        '400':
          description: Bad request
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /payments/{id}:
    get:
      summary: Get payment
      description: Retrieve payment details
      operationId: getPayment
      tags:
        - payments
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: Payment ID
      responses:
        '200':
          description: Success
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Payment'
        '404':
          description: Payment not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /refunds:
    post:
      summary: Create refund
      description: Process a refund for a payment
      operationId: createRefund
      tags:
        - refunds
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/RefundRequest'
      responses:
        '201':
          description: Refund created
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Refund'
        '400':
          description: Bad request
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /webhooks:
    post:
      summary: Register webhook
      description: Register a webhook endpoint
      operationId: createWebhook
      tags:
        - webhooks
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/WebhookRequest'
      responses:
        '201':
          description: Webhook registered
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Webhook'
        '400':
          description: Bad request
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
components:
  schemas:
    PaymentRequest:
      type: object
      required:
        - amount
        - currency
        - paymentMethod
      properties:
        amount:
          type: number
          description: Payment amount
        currency:
          type: string
          description: Currency code (USD, EUR, etc.)
        paymentMethod:
          type: string
          description: Payment method ID
        description:
          type: string
          description: Payment description
    Payment:
      type: object
      properties:
        id:
          type: string
          description: Payment ID
        amount:
          type: number
          description: Payment amount
        currency:
          type: string
          description: Currency code
        status:
          type: string
          enum: [pending, completed, failed, refunded]
          description: Payment status
        createdAt:
          type: string
          format: date-time
          description: Payment creation timestamp
    RefundRequest:
      type: object
      required:
        - paymentId
        - amount
      properties:
        paymentId:
          type: string
          description: Original payment ID
        amount:
          type: number
          description: Refund amount
        reason:
          type: string
          description: Refund reason
    Refund:
      type: object
      properties:
        id:
          type: string
          description: Refund ID
        paymentId:
          type: string
          description: Original payment ID
        amount:
          type: number
          description: Refund amount
        status:
          type: string
          enum: [pending, completed, failed]
          description: Refund status
        createdAt:
          type: string
          format: date-time
    WebhookRequest:
      type: object
      required:
        - url
        - events
      properties:
        url:
          type: string
          format: uri
          description: Webhook endpoint URL
        events:
          type: array
          items:
            type: string
          description: Event types to subscribe to
    Webhook:
      type: object
      properties:
        id:
          type: string
          description: Webhook ID
        url:
          type: string
          format: uri
          description: Webhook endpoint URL
        events:
          type: array
          items:
            type: string
          description: Subscribed events
        active:
          type: boolean
          description: Webhook active status
  securitySchemes:
    ApiKeyAuth:
      type: apiKey
      in: header
      name: X-API-Key`,

  'parcel-tracking': `openapi: 3.0.0
info:
  title: Parcel Tracking API
  version: 1.0.0
  description: |
    Track shipments and get real-time delivery status updates.
    
    ## Rate Limiting
    2000 requests per hour per API key.
  contact:
    name: Tracking Support
    email: tracking@example.com
servers:
  - url: https://api.tracking.example.com/v1
    description: Production server
tags:
  - name: tracking
    description: Tracking operations
  - name: shipments
    description: Shipment management
paths:
  /track/{trackingNumber}:
    get:
      summary: Track parcel
      description: Get tracking information for a parcel
      operationId: trackParcel
      tags:
        - tracking
      parameters:
        - name: trackingNumber
          in: path
          required: true
          schema:
            type: string
          description: Tracking number
      responses:
        '200':
          description: Success
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/TrackingInfo'
        '404':
          description: Tracking number not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /shipments:
    post:
      summary: Create shipment
      description: Create a new shipment
      operationId: createShipment
      tags:
        - shipments
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/ShipmentRequest'
      responses:
        '201':
          description: Shipment created
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Shipment'
        '400':
          description: Bad request
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /shipments/{id}:
    get:
      summary: Get shipment
      description: Get shipment details
      operationId: getShipment
      tags:
        - shipments
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: Shipment ID
      responses:
        '200':
          description: Success
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/Shipment'
        '404':
          description: Shipment not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
  /shipments/{id}/events:
    post:
      summary: Add tracking event
      description: Add a new tracking event to shipment
      operationId: addTrackingEvent
      tags:
        - shipments
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
          description: Shipment ID
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/TrackingEvent'
      responses:
        '201':
          description: Event added
        '400':
          description: Bad request
        '404':
          description: Shipment not found
        '401':
          description: Unauthorized
      security:
        - ApiKeyAuth: []
components:
  schemas:
    TrackingInfo:
      type: object
      properties:
        trackingNumber:
          type: string
          description: Tracking number
        status:
          type: string
          enum: [pending, in_transit, out_for_delivery, delivered, failed]
          description: Current status
        estimatedDelivery:
          type: string
          format: date-time
          description: Estimated delivery time
        events:
          type: array
          items:
            $ref: '#/components/schemas/TrackingEvent'
          description: Tracking events history
    ShipmentRequest:
      type: object
      required:
        - origin
        - destination
        - weight
      properties:
        origin:
          $ref: '#/components/schemas/Address'
        destination:
          $ref: '#/components/schemas/Address'
        weight:
          type: number
          description: Package weight in kg
        dimensions:
          $ref: '#/components/schemas/Dimensions'
    Shipment:
      type: object
      properties:
        id:
          type: string
          description: Shipment ID
        trackingNumber:
          type: string
          description: Tracking number
        origin:
          $ref: '#/components/schemas/Address'
        destination:
          $ref: '#/components/schemas/Address'
        status:
          type: string
          enum: [pending, in_transit, out_for_delivery, delivered, failed]
        createdAt:
          type: string
          format: date-time
    TrackingEvent:
      type: object
      required:
        - status
        - location
        - timestamp
      properties:
        status:
          type: string
          description: Event status
        location:
          type: string
          description: Event location
        timestamp:
          type: string
          format: date-time
          description: Event timestamp
        description:
          type: string
          description: Event description
    Address:
      type: object
      required:
        - street
        - city
        - country
        - postalCode
      properties:
        street:
          type: string
        city:
          type: string
        state:
          type: string
        country:
          type: string
        postalCode:
          type: string
    Dimensions:
      type: object
      properties:
        length:
          type: number
        width:
          type: number
        height:
          type: number
        unit:
          type: string
          enum: [cm, in]
  securitySchemes:
    ApiKeyAuth:
      type: apiKey
      in: header
      name: X-API-Key`
};
